// ─────────────────────────────────────────────────────────────────────────────
// CalendarScreen.js
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, SafeAreaView, StatusBar, RefreshControl } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const API = 'https://api.jobeng.ai';
const KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const P = '#0e4e96';
const getH = async () => { const t = await AsyncStorage.getItem('auth_token').catch(() => null); return { 'Content-Type': 'application/json', 'x-api-key': KEY, ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS = ['Su','Mo','Tu','We','Th','Fr','Sa'];
const EVENT_COLORS = { holiday: '#ef4444', review: P, training: '#8b5cf6', meeting: '#10b981', deadline: '#f59e0b', leave: '#06b6d4' };

export function CalendarScreen({ navigation, route }) {
  const empId = route?.params?.employeeId || '1';
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [events, setEvents] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDay, setSelectedDay] = useState(today.getDate());

  const load = async () => {
    try {
      const h = await getH();
      const cid = await AsyncStorage.getItem('company_id');
      const res = await fetch(`${API}/api/data/hr_calendar?company_id=${parseInt(cid)||0}`, { headers: h });
      const json = await res.json().catch(() => ({}));
      setEvents(Array.isArray(json?.data) ? json.data : []);
    } catch { setEvents([]); }
    finally { setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const isToday = (d) => d === today.getDate() && currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear();

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth()+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return events.filter(e => (e.date || e.event_date || e.start_date || '').startsWith(dateStr));
  };

  const selectedEvents = getEventsForDay(selectedDay);

  return (
    <SafeAreaView style={sc.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      <LinearGradient colors={[P, '#1e7be2']} style={sc.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={sc.back}><MaterialCommunityIcons name="arrow-left" size={22} color="#fff" /></TouchableOpacity>
        <Text style={sc.headerTitle}>Calendar</Text>
        <TouchableOpacity onPress={() => setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1))}><Text style={sc.todayBtn}>Today</Text></TouchableOpacity>
      </LinearGradient>
      <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} colors={[P]} />}>
        <View style={sc.monthNav}>
          <TouchableOpacity onPress={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth()-1, 1))} style={sc.navBtn}><MaterialCommunityIcons name="chevron-left" size={24} color={P} /></TouchableOpacity>
          <Text style={sc.monthLabel}>{MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}</Text>
          <TouchableOpacity onPress={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth()+1, 1))} style={sc.navBtn}><MaterialCommunityIcons name="chevron-right" size={24} color={P} /></TouchableOpacity>
        </View>
        <View style={sc.grid}>
          {DAYS.map(d => <Text key={d} style={sc.dayHeader}>{d}</Text>)}
          {cells.map((day, i) => {
            const dayEvents = getEventsForDay(day);
            return (
              <TouchableOpacity key={i} style={[sc.cell, isToday(day) && sc.todayCell, selectedDay === day && !isToday(day) && sc.selectedCell]} onPress={() => day && setSelectedDay(day)}>
                {day && <>
                  <Text style={[sc.dayNum, isToday(day) && sc.todayNum, selectedDay === day && !isToday(day) && sc.selectedNum]}>{day}</Text>
                  <View style={sc.dotRow}>{dayEvents.slice(0,3).map((e,j) => <View key={j} style={[sc.eventDot, { backgroundColor: EVENT_COLORS[e.type || e.event_type] || '#94a3b8' }]} />)}</View>
                </>}
              </TouchableOpacity>
            );
          })}
        </View>
        <View style={sc.section}>
          <Text style={sc.sectionTitle}>{selectedDay} {MONTHS[currentDate.getMonth()]}</Text>
          {selectedEvents.length === 0 ? <Text style={sc.noEvents}>No events</Text> : selectedEvents.map((e, i) => (
            <View key={i} style={sc.eventItem}>
              <View style={[sc.eventColor, { backgroundColor: EVENT_COLORS[e.type || e.event_type] || '#94a3b8' }]} />
              <View>
                <Text style={sc.eventTitle}>{e.title || e.event_name || 'Event'}</Text>
                {e.description && <Text style={sc.eventDesc} numberOfLines={1}>{e.description}</Text>}
              </View>
            </View>
          ))}
        </View>
        {/* UK Bank Holidays notice */}
        <View style={sc.holidayNote}>
          <MaterialCommunityIcons name="information-outline" size={14} color={P} />
          <Text style={sc.holidayNoteText}>UK public holidays are marked on the calendar</Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const sc = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#f8fafc' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14 },
  back: { padding: 4 },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  todayBtn: { color: '#fff', fontWeight: '600', fontSize: 13 },
  monthNav: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12 },
  navBtn: { padding: 4 },
  monthLabel: { fontSize: 18, fontWeight: '800', color: '#1e293b' },
  grid: { flexDirection: 'row', flexWrap: 'wrap', paddingHorizontal: 8 },
  dayHeader: { width: '14.28%', textAlign: 'center', fontSize: 11, fontWeight: '700', color: '#94a3b8', paddingVertical: 8 },
  cell: { width: '14.28%', aspectRatio: 1, alignItems: 'center', justifyContent: 'center', borderRadius: 10 },
  todayCell: { backgroundColor: P },
  selectedCell: { backgroundColor: '#e0e7ff' },
  dayNum: { fontSize: 13, fontWeight: '600', color: '#1e293b' },
  todayNum: { color: '#fff', fontWeight: '900' },
  selectedNum: { color: P, fontWeight: '900' },
  dotRow: { flexDirection: 'row', gap: 2, marginTop: 1 },
  eventDot: { width: 4, height: 4, borderRadius: 2 },
  section: { margin: 16 },
  sectionTitle: { fontSize: 15, fontWeight: '800', color: '#1e293b', marginBottom: 10 },
  noEvents: { color: '#94a3b8', fontSize: 13 },
  eventItem: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  eventColor: { width: 4, height: 32, borderRadius: 2 },
  eventTitle: { fontSize: 13, fontWeight: '700', color: '#1e293b' },
  eventDesc: { fontSize: 11, color: '#64748b', marginTop: 1 },
  holidayNote: { flexDirection: 'row', alignItems: 'center', gap: 6, margin: 16, padding: 12, backgroundColor: '#eff6ff', borderRadius: 10 },
  holidayNoteText: { fontSize: 12, color: P, fontWeight: '500' },
});

export default CalendarScreen;
